import React, { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactFormData } from '../../types';
import { ordersAPI } from '../../services/api';
import './ContactForm.css';

declare global {
    interface Window {
        ym?: (id: number, action: string, goal: string) => void;
    }
}

const ContactForm: React.FC = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        company: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            // Отправка заявки через API
            await ordersAPI.create({
                customer_name: formData.name,
                customer_email: formData.email,
                customer_phone: formData.company, // используем поле company как телефон
                message: formData.message,
            });

            // Симуляция отправки
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Yandex Metrika goal
            if (window.ym) {
                window.ym(12345678, 'reachGoal', 'contact_form_submit');
            }

            setSubmitStatus('success');
            setFormData({ name: '', email: '', company: '', message: '' });
        } catch (error) {
            console.error('Error submitting order:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
                <label htmlFor="name">{t('contact.form.name')}</label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={t('contact.form.name')}
                />
            </div>

            <div className="form-group">
                <label htmlFor="email">{t('contact.form.email')}</label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={t('contact.form.email')}
                />
            </div>

            <div className="form-group">
                <label htmlFor="company">{t('contact.form.company')}</label>
                <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder={t('contact.form.company')}
                />
            </div>

            <div className="form-group">
                <label htmlFor="message">{t('contact.form.message')}</label>
                <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="form-textarea"
                    placeholder={t('contact.form.message')}
                />
            </div>

            <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
            >
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
            </button>

            {submitStatus === 'success' && (
                <div className="alert alert-success">
                    {t('contact.form.success')}
                </div>
            )}

            {submitStatus === 'error' && (
                <div className="alert alert-error">
                    {t('contact.form.error')}
                </div>
            )}
        </form>
    );
};

export default ContactForm;
